import { createContext, useContext, useEffect, useState } from 'react';
import { settingsAPI } from '../utils/api';

const ThemeContext = createContext(null);

const defaultTheme = {
  primaryColor: '#FF6B00',
  secondaryColor: '#1A3C6E',
  backgroundColor: '#F7F8FC',
  fontChoice: 'Nunito',
  logoUrl: null,
  faviconUrl: null,
  whatsappOtpEnabled: false
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = async () => {
    try {
      const res = await settingsAPI.getTheme();
      const t = res.data.data;
      setTheme(t);
      applyTheme(t);
    } catch {
      applyTheme(defaultTheme);
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (t) => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', t.primaryColor);
    root.style.setProperty('--brand-secondary', t.secondaryColor);
    root.style.setProperty('--bg', t.backgroundColor);

    // Derive light variants
    root.style.setProperty('--brand-primary-light', hexToRgba(t.primaryColor, 0.1));
    root.style.setProperty('--brand-secondary-light', hexToRgba(t.secondaryColor, 0.08));

    // Update favicon if set
    if (t.faviconUrl) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = t.faviconUrl;
      document.head.appendChild(link);
    }
  };

  const updateTheme = (newTheme) => {
    setTheme(prev => ({ ...prev, ...newTheme }));
    applyTheme({ ...theme, ...newTheme });
  };

  return (
    <ThemeContext.Provider value={{ theme, loading, updateTheme, reloadTheme: loadTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const hexToRgba = (hex, alpha) => {
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch {
    return hex;
  }
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export default ThemeContext;
